/**
 * CampaignPlanReviewPage — interactive Plan Reviewer.
 *
 * Split-view layout:
 *   - Left: PlanDashboard (campaign summary, line-item hierarchy w/ inline
 *           editing, warnings panel)
 *   - Right: CopilotSidebar (agent-authored messages + free-text Q&A)
 *   - Top:   LaunchBar (session status, token/cost readout, Save + Launch)
 *
 * Edit model (as of the "direct inline edits" PR):
 *   Inline edits mutate a local `editedPlan` copy and persist via the
 *   existing PUT /api/v2/campaigns/:id/planning-review endpoint — the
 *   SAME endpoint CampaignPlanningReview uses. Edits do NOT go through
 *   the agent. The agent still answers questions about the plan, but it's
 *   read-only for mutations. The reviewer owns the edit state.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAgentSession, STATUS } from '../hooks/useAgentSession';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { isSuperAdmin } from '../utils/permissions';
import { normalizeForDashboard } from './planReview/planSelectors';

import PlanDashboard from './planReview/PlanDashboard';
import CopilotSidebar from './planReview/CopilotSidebar';
import LaunchBar from './planReview/LaunchBar';

import './CampaignPlanReviewPage.css';

// MMGY pipeline processing_id format, verified against historical artifacts:
//   <orchestration_chat_id>_<orchestration_message_id>_campaign
function derivePlanContext(campaign, searchParams) {
  const overridePid = searchParams.get('processingId');
  const overridePlatform = searchParams.get('platform');
  if (overridePid && overridePlatform) {
    return { processingId: overridePid, platform: overridePlatform };
  }
  if (!campaign) return { processingId: null, platform: null };
  const platform = (overridePlatform || campaign.destination || '').toLowerCase();
  const chatId = campaign.orchestration_chat_id;
  const messageId = campaign.orchestration_message_id;
  const processingId = overridePid
    || (chatId && messageId ? `${chatId}_${messageId}_campaign` : null);
  return { processingId, platform };
}

// Minimal RFC 6901 JSON Pointer setter. Mutates a deep-cloned plan to set
// the value at `path`; returns { ok, oldValue } or { ok: false, reason }.
function setAtPointer(obj, path, value) {
  if (!path || !path.startsWith('/')) return { ok: false, reason: `Invalid path: ${path}` };
  const tokens = path.slice(1).split('/').map(t => t.replace(/~1/g, '/').replace(/~0/g, '~'));
  let cursor = obj;
  for (let i = 0; i < tokens.length - 1; i++) {
    const t = tokens[i];
    if (cursor == null) return { ok: false, reason: `Missing parent at ${tokens.slice(0, i + 1).join('/')}` };
    if (Array.isArray(cursor)) {
      const idx = Number(t);
      if (!Number.isInteger(idx) || idx < 0 || idx >= cursor.length) {
        return { ok: false, reason: `Bad array index ${t}` };
      }
      cursor = cursor[idx];
    } else {
      cursor = cursor[t];
    }
  }
  const last = tokens[tokens.length - 1];
  if (cursor == null) return { ok: false, reason: `Missing parent for ${last}` };
  let oldValue;
  if (Array.isArray(cursor)) {
    const idx = Number(last);
    if (!Number.isInteger(idx)) return { ok: false, reason: `Bad array index ${last}` };
    oldValue = cursor[idx];
    cursor[idx] = value;
  } else {
    oldValue = cursor[last];
    cursor[last] = value;
  }
  return { ok: true, oldValue };
}

const CampaignPlanReviewPage = () => {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;

  const [campaign, setCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [restarting, setRestarting] = useState(false);

  // Edit state.
  const [editedPlan, setEditedPlan] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // 1. Fetch the campaign so we can derive processingId + platform.
  useEffect(() => {
    if (!campaignId || !accountId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
          params: { accountId, userId },
        });
        if (!cancelled) setCampaign(resp.data);
      } catch (err) {
        if (!cancelled) setBootError(`Couldn't load campaign: ${err.response?.data?.error || err.message}`);
      } finally {
        if (!cancelled) setCampaignLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, accountId, userId]);

  const { processingId, platform } = derivePlanContext(campaign, searchParams);
  const contextReady = Boolean(processingId && platform);
  const platformSupported = ['ttd', 'dv360'].includes(platform);

  // 2. Create the agent session when we have enough context.
  useEffect(() => {
    if (!contextReady || !platformSupported || !accountId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.post(
          getApiUrl(`/api/v2/agent-sessions`),
          { campaignId, processingId, platform, accountId, userId },
          { params: { accountId, userId } },
        );
        if (!cancelled) setSessionId(resp.data.sessionId);
      } catch (err) {
        if (!cancelled) setBootError(err.response?.data?.error || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, processingId, platform, accountId, userId]);

  const session = useAgentSession(sessionId, accountId, userId);

  // 3. Seed editedPlan from the agent's plan snapshot on arrival.
  //    Only replace if we don't already have an editedPlan (first arrival)
  //    OR the session's currentPlan changes identity AND we have no local
  //    edits (external update, e.g. after a separate save).
  useEffect(() => {
    if (!session.currentPlan) return;
    setEditedPlan(prev => {
      if (prev == null) return structuredClone(session.currentPlan);
      if (!hasEdits) return structuredClone(session.currentPlan);
      return prev; // keep local edits
    });
  }, [session.currentPlan, hasEdits]);

  // 4. Direct inline edit — mutates editedPlan locally, no agent involved.
  const handleEditField = useCallback(({ path, newValue }) => {
    if (!path) return;
    setEditedPlan(prev => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const result = setAtPointer(next, path, newValue);
      if (!result.ok) {
        setSaveError(`Couldn't apply edit at ${path}: ${result.reason}`);
        return prev;
      }
      setSaveError(null);
      setHasEdits(true);
      return next;
    });
  }, []);

  // 5. Persist edits via the existing /planning-review PUT endpoint.
  //    Mirrors CampaignPlanningReview.handleSaveEdits — same server route,
  //    same revision-preservation semantics.
  const handleSaveEdits = useCallback(async () => {
    if (!editedPlan || !hasEdits) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(
        getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`),
        { editedLauncherResult: editedPlan, accountId, userId },
      );
      setHasEdits(false);
    } catch (err) {
      setSaveError(`Save failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  }, [campaignId, editedPlan, hasEdits, accountId, userId]);

  const handleLaunch = useCallback(async () => {
    // Save any pending edits first, then navigate to the campaign detail
    // page where the existing orchestration polling takes over.
    if (hasEdits) await handleSaveEdits();
    navigate(`/campaigns/${campaignId}`);
  }, [hasEdits, handleSaveEdits, campaignId, navigate]);

  // Archive the current session and mount a fresh one against the same
  // campaign. The auto-resume query in POST /api/v2/agent-sessions keys off
  // status IN ('active','idle'), so terminating the old row is enough to
  // force the create branch. Unsaved edits are persisted first so they're
  // not lost — they live on the campaign record, not the session.
  const handleNewSession = useCallback(async () => {
    if (!sessionId || restarting) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Start a new session? The current conversation will be archived.')) return;

    setRestarting(true);
    try {
      if (hasEdits) {
        try { await handleSaveEdits(); } catch (_) { /* save error already surfaced in state */ }
      }

      await api.post(
        getApiUrl(`/api/v2/agent-sessions/${sessionId}/terminate`),
        { accountId, userId },
        { params: { accountId, userId } },
      );

      // Clear local state so the new session starts from a blank slate.
      setSessionId(null);
      setEditedPlan(null);
      setHasEdits(false);
      setSaveError(null);

      const resp = await api.post(
        getApiUrl(`/api/v2/agent-sessions`),
        { campaignId, processingId, platform, accountId, userId },
        { params: { accountId, userId } },
      );
      setSessionId(resp.data.sessionId);
    } catch (err) {
      setBootError(`Couldn't start a new session: ${err.response?.data?.error || err.message}`);
    } finally {
      setRestarting(false);
    }
  }, [sessionId, restarting, hasEdits, handleSaveEdits, campaignId, processingId, platform, accountId, userId]);

  // Edit count for the launch bar chip. Counts distinct root-level paths
  // that have been modified at least once (approximation — good enough for
  // the chip, not used for persistence logic).
  const editCount = useMemo(() => (hasEdits ? 'unsaved' : 0), [hasEdits]);

  // Imperative bridge from the dashboard's per-row "@" buttons to the
  // Copilot textarea. Inserting a reference is naturally a one-shot push,
  // not a steady-state value, so a ref + imperative method is a better fit
  // than a controlled prop (which would need a "consumed" callback to clear).
  const copilotRef = useRef(null);
  const handleReferenceLineItem = useCallback(({ path, displayName }) => {
    if (!path) return;
    // The agent's apply_plan_edit tool already speaks JSON Pointer paths,
    // so dropping the path verbatim is the most useful reference. The
    // quoted displayName is for the human reading the conversation back.
    const quoted = displayName ? ` "${String(displayName).replace(/"/g, '\\"')}"` : '';
    copilotRef.current?.insertReference(`@${path}${quoted}`);
  }, []);

  if (bootError) {
    return <div className="plan-review-error">Plan review could not start: {bootError}</div>;
  }
  if (campaignLoading) {
    return <div className="plan-review-loading">Loading campaign…</div>;
  }
  if (campaign && !contextReady) {
    return (
      <div className="plan-review-error">
        Plan review can't run for this campaign yet — the processing pipeline
        hasn't been launched or hasn't completed (no orchestration IDs on the
        campaign record).
      </div>
    );
  }
  if (contextReady && !platformSupported) {
    return (
      <div className="plan-review-error">
        Plan review only supports <code>ttd</code> and <code>dv360</code>. This
        campaign's destination is <code>{platform}</code>.
      </div>
    );
  }
  if (!sessionId) {
    return <div className="plan-review-loading">Starting agent session…</div>;
  }

  return (
    <div className="plan-review-page">
      <LaunchBar
        sessionStatus={session.status}
        editCount={editCount}
        hasEdits={hasEdits}
        saving={saving}
        onSave={handleSaveEdits}
        onLaunch={handleLaunch}
        onInterrupt={session.interrupt}
        onNewSession={handleNewSession}
        restarting={restarting}
        warnings={session.warnings}
        usage={session.usage}
        showUsage={isSuperAdmin(user)}
      />
      {saveError && <div className="plan-review-save-error">{saveError}</div>}

      <div className="plan-review-body">
        <div className="plan-review-dashboard">
          <PlanDashboard
            plan={normalizeForDashboard(editedPlan || session.currentPlan)}
            warnings={session.warnings}
            onEditField={handleEditField}
            onReferenceLineItem={handleReferenceLineItem}
          />
        </div>
        <div className="plan-review-sidebar">
          <CopilotSidebar
            ref={copilotRef}
            status={session.status}
            messages={session.messages}
            toolCalls={session.toolCalls}
            onSend={session.sendMessage}
            onReconnect={session.connectionError ? session.reconnect : null}
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignPlanReviewPage;
